<script>
import { Component, Vue, Watch } from 'vue-property-decorator'
import { jtChartMethods } from '../shared'
import { theme } from './../../../styles/waste.project.json'

@Component({
  name: 'JtBaseBarLineChart',
  props: {
    data: {
      type: Array,
      required: false,
      default: []
    },
    shouldTranslateXAxisData: {
      type: Boolean,
      required: false,
      default: false
    },
    xAxisData: {
      type: Array,
      required: true
    },
    xAxisMax: {
      type: Number,
      required: false
    },
    xAxisMin: {
      type: Number,
      required: false,
      default: 0
    },
    xAxisName: {
      type: String,
      required: false
    },
    xAxisNameConst: {
      type: String,
      required: false
    },
    yAxisMax: {
      type: Number,
      required: false
    },
    yAxisMin: {
      type: Number,
      required: false
    },
    yAxisName: {
      type: String,
      required: false
    },
    yAxisNameConst: {
      type: String,
      required: false
    },
    horizontalBar: {
      type: Boolean,
      required: false,
      default: false
    },
    yAxisIndexNames: {
      type: Array,
      required: false,
      default: []
    }
  }
})
export default class JtBaseBarLineChart extends Vue {
  defaultEchartColorsForChartsSeries = []
  hasChartInitialized = false

  chartSettings = {
    title: {
      left: 'center'
    },
    grid: {
      left: 0,
      right: 25,
      containLabel: true
    },
    legend: {
      bottom: 10
    },
    tooltip: {
      axisPointer: {
        type: 'shadow',
        crossStyle: {
          color: '#999'
        }
      },
      transitionDuration: 0,
      formatter: function (params) {
        var tooltipText = ''
        params.forEach(function (param) {
          if (param.value != 0) {
            tooltipText += `${param.marker} ${param.seriesName}: ${param.value} <br/>`
          }
        })
        return tooltipText
      }
    },
    xAxis: [{
      axisLabel: {
        formatter: function (value) {
          return value.toLocaleString('pt-BR')
        }
      },
      type: (this.horizontalBar ? 'value' : 'category'),
      axisLine: { show: true },
      axisTick: { show: false },
      nameRotate: 270,
      nameTextStyle: {
        fontWeight: 800,
        align: 'right',
        verticalAlign: 'bottom'
      }
    }],
    yAxis: [
      {
        axisLabel: {
          formatter: function (value) {
            return value.toLocaleString('pt-BR')
          }
        },
        axisLine: { show: true },
        type: (this.horizontalBar ? 'category' : 'value'),
        nameTextStyle: {
          align: 'left',
          fontWeight: 800
        }
      }
    ],
    series: []
  }

  created () {
    this.chartSettings.xAxis.name = this.xAxisName || (this.xAxisNameConst ? this.$gettext(this.xAxisNameConst) : '')
    this.chartSettings.xAxis.data = this.shouldTranslateXAxisData ? this.xAxisData.map(xAxisDataConst => this.$gettext(xAxisDataConst)) : this.xAxisData
    this.chartSettings.yAxis[0].data = this.shouldTranslateXAxisData ? this.xAxisData.map(xAxisDataConst => this.$gettext(xAxisDataConst)) : this.xAxisData

    if (this.xAxisMax) this.chartSettings.xAxis.max = this.xAxisMax
    this.chartSettings.yAxis[0].name = this.yAxisName || (this.yAxisNameConst ? this.$gettext(this.yAxisNameConst) : '')
  }

  mounted () {
    this.$refs.chart.theme = theme
    setTimeout(() => {
      this.defaultEchartColorsForChartsSeries = jtChartMethods.getChartColor()
      this.hasChartInitialized = true
      this.amountYAxisIndexWatcher(this.yAxisIndexNames, this.data)
      this.xAxisDataWatcher(this.xAxisData)
      this.dataWatcher(this.data)
    }, 100)
  }

  addAdditionalSeriesSettings (serieObject) {
    return serieObject
  }

  createSerieObject (information, idx, idxForColorDefault) {
    let serieObject = {
      name: information.name || (information.nameConst ? this.$gettext(information.nameConst) : `${this.$gettext('NO_LABEL')}-${idx}`),
      type: (information.type != null ? information.type : 'bar'),
      yAxisIndex: (information.yAxisIndex != null ? information.yAxisIndex : 0),
      emphasis: {
        focus: 'series'
      },
      data: this.getSerieData(information.data),
      label: {
        show: true,
        formatter: function (params) {
          const label = params.value
          return label.toLocaleString('pt-BR')
        }
      },
      tooltip: {
        valueFormatter: function (value) {
          return value.toLocaleString('pt-BR')
        }
      }

    }

    if (information?.color) {
      serieObject.itemStyle = {
        color: information.color
      }
    }

    serieObject = this.addAdditionalSeriesSettings(serieObject, idxForColorDefault)

    return serieObject
  }

  getAxisData (xAxisData) {
    return this.shouldTranslateXAxisData ? xAxisData.map(xAxisDataConst => this.$gettext(xAxisDataConst)) : xAxisData
  }

  getSerieData (data) {
    if (data && data !== '-') {
      const dataAmount = data.length
      const xAxisAmount = this.xAxisData.length
      if (dataAmount === xAxisAmount) {
        return data
      } else if (dataAmount < xAxisAmount) {
        return [
          ...data,
          ...Array(xAxisAmount - dataAmount).fill(0)
        ]
      } else {
        return data.slice(0, xAxisAmount - dataAmount)
      }
    } else {
      return Array(this.xAxisData.length).fill(0)
    }
  }

  recreateChart (series, seriesNames) {
    const dynamicSettings = {
      ...this.chartSettings,
      series
    }
    dynamicSettings.legend.data = seriesNames
    dynamicSettings.xAxis.data = this.getAxisData(this.xAxisData)
    this.$refs.chart.setOption(dynamicSettings, true)
  }

  updateChartData (series, seriesNames) {
    this.$refs.chart.setOption({
      legend: {
        data: seriesNames
      },
      series
    })
  }

  @Watch('amountYAxisIndex', { deep: true })
  amountYAxisIndexWatcher (newYAxisIndexNames, data) {
    if (this.hasChartInitialized) {
      const yAxisDynamic = []

      for (let i = 0; i < newYAxisIndexNames.length; i++) {
        const yAxis = {
          axisLine: { show: true },
          type: (this.horizontalBar ? 'category' : 'value'),
          nameTextStyle: {
            align: 'left',
            fontWeight: 800
          }
        }
        yAxis.name = (newYAxisIndexNames[i] != null ? newYAxisIndexNames[i] : '')
        yAxisDynamic.push(yAxis)
      }

      const dynamicSettings = {
        yAxis: yAxisDynamic
      }

      this.$refs.chart.setOption(dynamicSettings)
    }
  }

  @Watch('xAxisData', { deep: true })
  xAxisDataWatcher (newXAxisData) {
    if (this.hasChartInitialized) {
      const dynamicSettings = {
        xAxis: {
          data: this.getAxisData(newXAxisData)
        }
      }
      this.$refs.chart.setOption(dynamicSettings)
    }
  }

  @Watch('data', { deep: true })
  dataWatcher (newData) {
    if (this.hasChartInitialized) {
      let newSeriesArray
      if (!newData || newData.length === 0) {
        newSeriesArray = []
      } else {
        newSeriesArray = []
        let idxForColorDefault = 0
        newData.forEach((serieInformations, idx) => {
          newSeriesArray.push(this.createSerieObject(serieInformations, idx, idxForColorDefault))
          if (!serieInformations?.color) {
            idxForColorDefault++
            if (idxForColorDefault === this.defaultEchartColorsForChartsSeries.length) {
              idxForColorDefault = 0
            }
          }
        })
      }

      const currentSeriesNames = this.$refs.chart.getOption().series.map(serie => serie.name)
      const newSeriesNames = newSeriesArray.map(newSerie => newSerie.name)
      const haveSeriesRemoved = currentSeriesNames.filter(serieName => !newSeriesNames.includes(serieName)).length > 0

      if (haveSeriesRemoved) {
        this.recreateChart(newSeriesArray, newSeriesNames)
      } else {
        this.updateChartData(newSeriesArray, newSeriesNames)
      }
    }
  }
}
</script>
